export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

/** The server rejected the Turnstile challenge (production only) — not a credentials/invitation problem. */
export function isTurnstileError(err: unknown): boolean {
  return err instanceof ApiError && err.code === "FORBIDDEN" && /turnstile/i.test(err.message);
}

async function parseError(res: Response): Promise<never> {
  let code = "INTERNAL";
  let message = res.statusText || "Request failed";
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } };
    if (body.error?.code) code = body.error.code;
    if (body.error?.message) message = body.error.message;
  } catch {
    /* non-JSON error */
  }
  throw new ApiError(res.status, code, message);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: "include", ...init });
  if (!res.ok) return parseError(res);
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") ?? "";
  if (!ct.includes("application/json")) return (await res.blob()) as unknown as T;
  return (await res.json()) as T;
}

function jsonInit(method: string, body?: unknown): RequestInit {
  return {
    method,
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  };
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, jsonInit("POST", body)),
  patch: <T>(path: string, body?: unknown) => request<T>(path, jsonInit("PATCH", body)),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  /** Open a credentialed same-origin document (e.g. the branded HTML report) in a new tab. */
  openTab: (path: string) => { window.open(path, "_blank", "noopener,noreferrer"); },
  /** Raw binary upload (documents/media): body is the file, filename via header. */
  upload: <T>(path: string, file: File) =>
    request<T>(path, { method: "POST", headers: { "Content-Type": file.type || "application/octet-stream", "X-Filename": file.name }, body: file }),
  /** Trigger a browser download of a private file streamed through the Worker. */
  download: async (path: string, filename: string) => {
    const res = await fetch(path, { credentials: "include" });
    if (!res.ok) return parseError(res);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
};
